export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export type SemanticSectionType =
  'station' | 'normal' | 'lift' | 'launch' | 'brake' | 'photo' | (string & {});

export interface SemanticSection {
  readonly id: string;
  readonly type: SemanticSectionType;
  readonly startDistance: number;
  readonly endDistance: number;
  readonly parameters?: Readonly<Record<string, number | string | boolean>>;
}

export interface BankKey {
  readonly distance: number;
  readonly value: number;
  readonly slope?: number;
}

export interface BSplineDefinition {
  readonly controlPoints: readonly Vec3[];
  readonly degree?: 3;
  readonly knots?: readonly number[];
  readonly closed?: boolean;
}

export interface TrackDefinition extends BSplineDefinition {
  readonly id?: string;
  readonly bank?: readonly BankKey[];
  readonly sections?: readonly SemanticSection[];
}

export interface TrackSample {
  readonly distance: number;
  readonly u: number;
  readonly position: Vec3;
  readonly derivative: Vec3;
  readonly secondDerivative: Vec3;
  readonly tangent: Vec3;
  readonly curvature: Vec3;
  readonly rmfUp: Vec3;
  readonly rmfRight: Vec3;
  readonly bank: number;
  readonly up: Vec3;
  readonly right: Vec3;
  readonly section: SemanticSection | null;
}

export interface TrackSampler {
  readonly length: number;
  readonly closed: boolean;
  readonly valid: boolean;
  readonly issues: readonly TrackValidationIssue[];
  sampleByDistance(distance: number): TrackSample;
}

export interface TrackValidationIssue {
  readonly code: string;
  readonly message: string;
  readonly distance?: number;
  readonly controlPointIndex?: number;
}

export interface AdaptiveTableOptions {
  readonly maxSpacing?: number;
  readonly maxChordError?: number;
  readonly maxTangentAngle?: number;
  readonly maxDepth?: number;
}

export interface ArcLengthTable {
  readonly count: number;
  readonly length: number;
  readonly closed: boolean;
  readonly distance: Float64Array;
  readonly u: Float64Array;
  readonly position: Float64Array;
  readonly derivative: Float64Array;
  readonly secondDerivative: Float64Array;
  readonly tangent: Float64Array;
  readonly curvature: Float64Array;
  readonly rmfUp: Float64Array;
  readonly rmfRight: Float64Array;
  readonly bank: Float64Array;
  readonly up: Float64Array;
  readonly right: Float64Array;
  readonly sectionIndex: Int32Array;
  readonly sections: readonly SemanticSection[];
}

export interface TrackCompilerCounters {
  readonly builds: number;
  readonly spanEvaluations: number;
  readonly spanCacheHits: number;
  readonly invalidatedSpans: number;
}

export class TrackBuildError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly details: Readonly<Record<string, number | string>> = {},
  ) {
    super(message);
    this.name = 'TrackBuildError';
  }
}
